import React, { useEffect, useRef, useState } from 'react';

import CartApi from '../../api/cart';
import analytics from '../../analytics';
import { SideMenu } from '../../navigation/side-menu';
import Rejoiner from '../../api/rejoiner';
import CartHandler from '../../cart-popup/cart-handler';

import transformItems from './transform-items';
import { CART_EVENTS } from '../../custom-events/cart';
import { CART_KEYS } from './constants/locale-storage';
import { checkHasSubscription } from './helpers/checkHasSubscription';
import { checkIsUpsellOnlyPage, checkIsRegularCartPage } from './helpers/checkPage';

export const CartContext = React.createContext({});

export const UPSELL_PRODUCTS = [
  45185080000681, // +Caffeine Shot Peach
  45185068007593, // +Caffeine Shot Green Apple
  46342144295081, // +Caffeine Shot Raspberry Lemonade
  47219288539305, // +Caffeine Shot Blue Raspberry
  46784916750505, // +Caffeine Shot Grape
  46427483570345, // No Caffeine Multiserving Bottle
  45185080197289, // No Caffeine Shot Peach
  46342120833193, // No Caffeine Shot Original
  45540493787305, // No Caffeine Shot Green Apple
  46342119325865, // No Caffeine Shot Raspberry Lemonade
  47219228704937, // No Caffeine Shot Blue Raspberry
  47219278151849 // No Caffeine Shot Grape
];

export const getProducts = (products) => (items) => items.filter(({ id }) => products.includes(id));
export const getUpsellProducts = getProducts(UPSELL_PRODUCTS);

const cartApi = new CartApi();
const sideMenu = new SideMenu();
const rejoiner = new Rejoiner();
const cartHandler = new CartHandler();

const CartContextProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [pendingActions, setPendingActions] = useState(() => new Set());
  const pendingRef = useRef(new Set());
  const isRegularCartPage = checkIsRegularCartPage();

  const isPending = (key) => pendingActions.has(key);

  const flushPending = () => setPendingActions(new Set(pendingRef.current));

  const runPending = async (key, fn) => {
    if (pendingRef.current.has(key)) return undefined;

    pendingRef.current.add(key);
    flushPending();
    try {
      return await fn();
    } finally {
      pendingRef.current.delete(key);
      flushPending();
    }
  };

  const closeCart = () => {
    sideMenu.hide('cart-popup-container');
  };

  const updateCartData = ({ data }) => {
    analytics.googleTagCart({ cartData: data });
    setCart({
      ...data,
      items: transformItems({ items: data.items })
    });
    cartHandler.handle({ itemCount: data.item_count });
  };

  const applyCartUpdate = async (data) => {
    let nextData = data;

    if (isRegularCartPage) {
      const upsellProducts = getUpsellProducts(nextData.items);
      const hasSubscription = checkHasSubscription(nextData.items);

      if (hasSubscription && upsellProducts.length > 0) {
        ({ data: nextData } = await cartApi.removeBundle({ items: upsellProducts }));
      }
    }

    updateCartData({ data: nextData });
    rejoiner.handleChange({ cartData: nextData });
  };

  const fetchCart = async () => {
    const { data } = await cartApi.getCartData();

    await applyCartUpdate(data);
  };

  const addItem = ({
    quantity,
    variantId,
    isSubscription
  }) => runPending(`add:${variantId}`, async () => {
    await cartApi.addCartItem({
      quantity,
      variantId,
      isSubscription
    });

    await fetchCart();
  });

  const removeItem = ({ key }) => runPending(`remove:${key}`, async () => {
    let { data } = await cartApi.removeCartItem({ key: String(key) });

    if (isRegularCartPage) {
      const upsellProducts = getUpsellProducts(data.items);
      const baseProducts = data.items.filter(({ id }) => !UPSELL_PRODUCTS.includes(id));

      if (baseProducts.length === 0 && upsellProducts.length > 0) {
        ({ data } = await cartApi.removeBundle({ items: upsellProducts }));
      }
    }

    await applyCartUpdate(data);
  });

  const cleanCart = async () => {
    try {
      const { data: { items }} = await cartApi.getCartData();

      await cartApi.removeBundle({ items });

      await fetchCart();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const updateItem = ({
    quantity,
    variantId,
    key,
    isSubscription
  }) => runPending(`qty:${key}`, async () => {
    const { data } = await cartApi.changeCartItem({
      id: String(key),
      variantId,
      quantity,
      isSubscription
    });

    if (quantity === 0) {
      rejoiner.handleRemove({
        variantId,
        cartData: data
      });
    }

    await applyCartUpdate(data);
  });

  const toggleSubscription = ({
    item,
    isSubscription
  }) => runPending(`sub:${item.key}`, async () => {
    const { data } = await cartApi.changeCartItem({
      id: String(item.key),
      variantId: item.variant_id,
      quantity: item.quantity,
      isSubscription
    });

    await applyCartUpdate(data);
  });

  useEffect(() => {
    const checkAndClean = async () => {
      if (localStorage.getItem(CART_KEYS.SHOULD_CLEANUP) === 'true') {
        localStorage.setItem(CART_KEYS.SHOULD_CLEANUP, 'false');

        await cleanCart();
      }
    };

    checkAndClean();
  }, []);

  useEffect(() => {
    document.addEventListener(CART_EVENTS.OPEN, fetchCart);
    document.addEventListener(CART_EVENTS.UPDATE, fetchCart);

    return () => {
      document.removeEventListener(CART_EVENTS.OPEN, fetchCart);
      document.removeEventListener(CART_EVENTS.UPDATE, fetchCart);
    };
  }, []);

  useEffect(() => {
    if (checkIsUpsellOnlyPage()) {
      localStorage.setItem(CART_KEYS.SHOULD_CLEANUP, 'true');
    }
  }, []);

  return (
    <CartContext.Provider
      value={{
        cart,
        addItem,
        removeItem,
        fetchCart,
        toggleSubscription,
        updateItem,
        closeCart,
        updateCartData,
        isPending
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContextProvider;
